import React, { useState, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSprings, animated, to } from 'react-spring';
import swap from 'lodash-move';

const styles = {
  position: 'absolute',
  width: '100%',
  height: '80px',
  marginLeft: '0px',
  overflow: 'visible',
  pointerEvents: 'auto',
  transformOrigin: '50% 50% 0px',
  bordeRadius: '5px',
  color: 'white',
  paddingLeft: '45px',
  fontSize: '14.5px',
  background: '#c8c8c8',
  textTransform: 'uppercase',
  letterSpacing: '0px',
  boxShadow: 'rgba(0, 0, 0, .6) 0px 1px 1px 0px',
};

const zoneItem = {
  url: '',
  title: '',
  text: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
};

const rowHeight = 85;
const clamp = (num, cl, higher) => (higher ? Math.min(Math.max(num, cl), higher) : Math.min(num, cl));
const fn = (order, down, originalIndex, currIndex, y) => (ind) => {
  if (down && ind === originalIndex) {
    return {
      y: currIndex * rowHeight + y,
      scale: 1,
      zIndex: '1',
      shadow: 15,
      immediate: n => n === 'y' || n === 'zIndex',
    };
  }
  return {
    y: order.indexOf(ind) * rowHeight,
    scale: 1,
    zIndex: '0',
    shadow: 1,
    immediate: false,
  };
};
const zoneItems = ({
  components, activeLanguage, languages, handlerSetComponentsInParent,
}) => {
  const [order, setOrder] = useState(components.map((_, i) => i));
  const [selected, setSelected] = useState([]);

  // Create springs, each corresponds to an item, controlling its transform, scale, etc.
  const [springs, springApi] = useSprings(selected.length, fn(order), [
    selected,
    order,
  ]);
    // Preps a gesture handler which returns drag-deltas, touched/clicked state, etc.

  const bind = useDrag(({ args: [originalIndex], down, movement: [, y] }) => {
    // Bunch of math to calculate current row and new order,
    const curIndex = order.indexOf(originalIndex);
    const curRow = clamp(
      Math.round((curIndex * rowHeight + y) / rowHeight),
      0,
      components.length - 1,
    );
    const newOrder = swap(order, curIndex, curRow);
    springApi.start(fn(newOrder, down, originalIndex, curIndex, y, order));
    if (!down) {
      setOrder(newOrder);
      // reorder selected and update component on PostContainer
      const newComponentsOrder = newOrder.map(i => selected[i]);
      handlerSetComponentsInParent(newComponentsOrder);
    }
  });
  const deleteItemHandler = (itemIndex) => {
    const newArr = selected;
    newArr.splice(itemIndex, 1);
    const ii = order.findIndex(num => num === itemIndex);
    const n = [
      ...order.slice(0, ii),
      ...order.slice(ii + 1).map(num => (num > 0 && num > ii ? num - 1 : num)),
    ];
    setSelected(newArr);
    setOrder(n);
    handlerSetComponentsInParent(newArr);
  };

  const inputHandlerLang = (e, i) => {
    const {
      value, name,
    } = e.target;
    let Item = {};

    if (name === 'text') {
      Item = {
        ...selected[i],
        [name]: {
          ...selected[i][name],
          [activeLanguage]: value,
        },
      };
    } else {
      Item = {
        ...selected[i],
        [name]: value,
      };
    }


    const newItem = [...selected];
    newItem[i] = Item;
    setSelected(newItem);
    handlerSetComponentsInParent(newItem);
  };

  const iHandler = index => (event) => {
    inputHandlerLang(event, index);
  };

  const addItemHandler = () => {
    const newArr = [zoneItem, ...selected];
    setOrder([...order, order.length]);
    setSelected(newArr);
    handlerSetComponentsInParent(newArr);
  };

  useEffect(() => {
    if (components.length) {
      setOrder(components.map((_, i) => i));
      setSelected(components);
    }
  }, [components]);


  return (
    <>
      <div
        className="content overflow-y-auto mb-4"
        style={{ height: selected.length * rowHeight, minHeight: '120px' }}
      >
        <button
          type="button"
          onClick={addItemHandler}
          className="font-americaMonoBold text-xs pl-10 pr-6 py-1 h-7 my-1 rounded-full uppercase bg-greyFilter flex flex-row items-center"
        >
          add new
          <span className="font-icon icon-add-button text-base pl-2" />
        </button>
        {selected.length && springs.map(({
          zIndex, shadow, y, scale,
        }, i) => (
          <animated.div
            {...bind(i)}
            key={i}
            style={{
              zIndex,
              // @use-gesture 10 needs this or the browser scrolls instead of dragging.
              touchAction: 'none',
              boxShadow: shadow.to(
                s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`,
              ),
              transform: to([y], (y, s) => `translate3d(0,${y}px,0)`),
            }}
          >
            <Item
              item={selected[i]}
              index={i}
              iHandler={iHandler}
              languages={languages}
              activeLanguage={activeLanguage}
              deleteItemHandler={deleteItemHandler}
            />
          </animated.div>
        ))}
      </div>
    </>
  );
};

const Item = ({
  item, index, iHandler, activeLanguage, languages, deleteItemHandler,
}) => (
  <div
    className="bg-greyFilter w-full rounded-lg flex items-center trans trans-opacity relative cursor-move"
    style={styles}
  >
    <div
      className="absolute right-0 top-0 flex items-end mt-1 mr-1 cursor-pointer z-10"
      onClick={() => deleteItemHandler(index)}
    >
      <span className="font-icon icon-close-solid text-16 text-white" />
    </div>
    <div className="h-full rounded-tl-lg rounded-bl-lg flex bg-greyFilterItems text-greyFilter absolute px-1 z-5 pin-l pin-t flex items-center justify-center text-30 pb-4px w-10">=</div>
    <div className="font-americaMonoBold text-black uppercase text-xs w-full flex flex-wrap items-center">
      <div className="w-full pr-8 mt-1">
        <input className="ml-1 mb-1 text-xxs w-full font-americaMonoRegular p-1 bg-white outline-none rounded-full" onChange={iHandler(index)} name="title" placeholder="Zone title" type="text" value={item.title || ''} />
        <input className="ml-1 mb-1 text-xxs w-full font-americaMonoRegular p-1 bg-white outline-none rounded-full" onChange={iHandler(index)} name="url" placeholder="Zone url" type="text" value={item.url || ''} />

        {languages.map(lang => (
          <div key={lang} className={`w-full ${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'block' : 'hidden'}`}>
            <input className="ml-1 mb-1 text-xxs w-full font-americaMonoRegular p-1 bg-white outline-none rounded-full" onChange={iHandler(index)} lang={lang} name="text" placeholder="Zone description" type="text" value={(item && item.text[lang]) || ''} />
          </div>
        ))}

      </div>
    </div>
  </div>
);

export default zoneItems;
